/**
 * Sprint 2.7.6 Step 6 — Outreach Pipeline section.
 *
 * Kanban-style columns: Targets | Drafted | Approved | Sent | Opened | Replied | Won
 * Collapsed Discarded strip at bottom.
 * Tier-gated: tier_1/free see a Pro upsell card.
 * 60-second background polling (Pro/Agency only).
 *
 * NOTE: clicking a draft card is a placeholder for Prompt 7 (approval/edit modal).
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { api } from '../api';
import type { BacklinkTarget, OutreachDraft, OutreachDraftStatus } from '../types';
import { OutreachDraftModal } from '../components/OutreachDraftModal';

// ── Constants ──────────────────────────────────────────────────────────────────

const POLL_MS = 60_000;
const TIER_RANK: Record<string, number> = { free: 0, tier_1: 1, tier_2: 2, tier_3: 3 };

const COLUMN_STATUSES: OutreachDraftStatus[] = [
  'drafted', 'approved', 'sent', 'opened', 'replied', 'won',
];

const COLUMN_HEADER_COLOR: Record<OutreachDraftStatus, string> = {
  drafted:   'text-gray-600',
  approved:  'text-amber-600',
  sent:      'text-blue-600',
  opened:    'text-purple-600',
  replied:   'text-indigo-600',
  won:       'text-green-600',
  discarded: 'text-gray-400',
};

const BADGE_COLORS: Record<OutreachDraftStatus, string> = {
  drafted:   'bg-gray-100 text-gray-600',
  approved:  'bg-amber-50 text-amber-600',
  sent:      'bg-blue-50 text-blue-600',
  opened:    'bg-purple-50 text-purple-600',
  replied:   'bg-indigo-50 text-indigo-600',
  won:       'bg-green-50 text-green-700',
  discarded: 'bg-gray-50 text-gray-400',
};

// Quick actions available from each status (target statuses to transition to)
const QUICK_ACTIONS: Record<OutreachDraftStatus, OutreachDraftStatus[]> = {
  drafted:   ['approved', 'discarded'],
  approved:  ['sent', 'drafted'],    // 'drafted' = revert for re-edit
  sent:      ['opened', 'replied', 'won'],
  opened:    ['replied', 'won'],
  replied:   ['won', 'discarded'],
  won:       [],
  discarded: [],
};

const ACTION_LABEL: Record<OutreachDraftStatus, string> = {
  approved:  'Approve',
  sent:      'Mark as Sent',
  opened:    'Mark as Opened',
  replied:   'Mark as Replied',
  won:       'Mark as Won',
  discarded: 'Discard',
  drafted:   'Edit',    // revert
};

// ── Helpers ────────────────────────────────────────────────────────────────────

function timeAgo(iso: string | null): string {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  const sec = Math.floor(ms / 1000);
  if (sec < 60) return 'just now';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} hr${hr !== 1 ? 's' : ''} ago`;
  const day = Math.floor(hr / 24);
  return `${day} day${day !== 1 ? 's' : ''} ago`;
}

function statusTimestamp(draft: OutreachDraft): string {
  const tsMap: Partial<Record<OutreachDraftStatus, string | null>> = {
    drafted:   draft.draftGeneratedAt,
    approved:  draft.approvedAt,
    sent:      draft.sentAt,
    opened:    draft.openedAt,
    replied:   draft.repliedAt,
    won:       draft.wonAt,
    discarded: draft.discardedAt,
  };
  const v = tsMap[draft.status] ?? null;
  return v ? `${draft.status} ${timeAgo(v)}` : '';
}

// ── Dismiss confirm modal ─────────────────────────────────────────────────────

function ConfirmDismissModal({
  target,
  onConfirm,
  onCancel,
}: {
  target: BacklinkTarget;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl p-6 max-w-sm w-full shadow-xl">
        <h3 className="font-semibold text-brand-ink mb-2">Dismiss target</h3>
        <p className="text-sm text-gray-500 mb-4">
          Remove <strong>{target.targetDomain}</strong> from your outreach targets?{' '}
          You can re-run discovery to surface it again.
        </p>
        <div className="flex gap-2 justify-end">
          <button
            onClick={onCancel}
            className="text-sm text-gray-500 px-4 py-2 rounded-lg hover:text-brand-ink"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="text-sm bg-gray-700 text-white px-4 py-2 rounded-lg hover:bg-brand-ink"
          >
            Dismiss
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Target card ───────────────────────────────────────────────────────────────

interface TargetCardProps {
  target: BacklinkTarget;
  generating: boolean;
  onGenerate: () => void;
  onDismiss: () => void;
}

function TargetCard({ target, generating, onGenerate, onDismiss }: TargetCardProps) {
  const snippet =
    target.rationale.length > 80
      ? target.rationale.slice(0, 80) + '…'
      : target.rationale;

  return (
    <div className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm space-y-2 min-w-0">
      <div className="flex items-start justify-between gap-2">
        <span className="font-semibold text-brand-ink text-sm truncate">
          {target.targetDomain}
        </span>
        {target.competitorAuthority !== null && (
          <span
            className="shrink-0 text-xs bg-amber-50 text-amber-600 px-2 py-0.5 rounded-full font-medium cursor-default"
            title="Competitor authority score (Moz Domain Authority equivalent)"
          >
            DA: {target.competitorAuthority}
          </span>
        )}
      </div>
      <p className="text-xs text-gray-500 leading-relaxed">{snippet}</p>
      <div className="flex gap-2 pt-1">
        <button
          onClick={onGenerate}
          disabled={generating}
          className="flex-1 text-xs font-medium bg-brand-green text-white rounded-lg px-3 py-1.5 hover:bg-brand-green-dark transition-colors disabled:opacity-60 flex items-center justify-center gap-1"
        >
          {generating ? (
            <>
              <span
                className="w-3 h-3 rounded-full border-2 border-white border-t-transparent animate-spin"
                aria-hidden="true"
              />
              Drafting…
            </>
          ) : (
            'Generate draft'
          )}
        </button>
        <button
          onClick={onDismiss}
          disabled={generating}
          className="text-xs text-gray-400 hover:text-gray-600 px-2 py-1.5 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-40"
        >
          Dismiss
        </button>
      </div>
    </div>
  );
}

// ── Draft card ────────────────────────────────────────────────────────────────

interface DraftCardProps {
  draft: OutreachDraft;
  targetDomain: string;
  transitioningTo: OutreachDraftStatus | null;
  onTransition: (toStatus: OutreachDraftStatus) => void;
  onCardClick: () => void;
}

function DraftCard({
  draft,
  targetDomain,
  transitioningTo,
  onTransition,
  onCardClick,
}: DraftCardProps) {
  const actions = QUICK_ACTIONS[draft.status];
  const ts = statusTimestamp(draft);
  const isTerminal = draft.status === 'won' || draft.status === 'discarded';

  const subject =
    draft.draftSubject.length > 60
      ? draft.draftSubject.slice(0, 60) + '…'
      : draft.draftSubject;

  const body =
    draft.draftBody.length > 140
      ? draft.draftBody.slice(0, 140) + '…'
      : draft.draftBody;

  return (
    <div
      className={`bg-white border border-gray-100 rounded-xl p-4 shadow-sm space-y-2 min-w-0 group transition-colors ${
        !isTerminal ? 'cursor-pointer hover:border-gray-200' : ''
      }`}
      onClick={!isTerminal ? onCardClick : undefined}
      role={!isTerminal ? 'button' : undefined}
      tabIndex={!isTerminal ? 0 : undefined}
      onKeyDown={(e) => {
        if (!isTerminal && e.key === 'Enter') onCardClick();
      }}
    >
      {/* Domain + status badge */}
      <div className="flex items-start justify-between gap-2">
        <span className="font-semibold text-brand-ink text-sm truncate">{targetDomain}</span>
        <span
          className={`shrink-0 text-xs px-2 py-0.5 rounded-full font-medium ${BADGE_COLORS[draft.status]}`}
        >
          {draft.status}
        </span>
      </div>

      {/* Subject */}
      <p className="text-xs font-medium text-brand-ink/70">{subject}</p>

      {/* Body preview */}
      <p className="text-xs text-gray-500 leading-relaxed">{body}</p>

      {/* Relative timestamp */}
      {ts && <p className="text-[10px] text-gray-400">{ts}</p>}

      {/* Quick actions — always visible on mobile, hover-reveal on desktop */}
      {actions.length > 0 && (
        <div
          className="flex flex-wrap gap-1.5 pt-2 border-t border-gray-50 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity"
          onClick={(e) => e.stopPropagation()}
        >
          {actions.map((toStatus) => (
            <button
              key={toStatus}
              onClick={() => onTransition(toStatus)}
              disabled={transitioningTo !== null}
              className={`text-xs px-2 py-1 rounded-lg transition-colors disabled:opacity-50 ${
                toStatus === 'discarded'
                  ? 'text-red-500 hover:bg-red-50'
                  : toStatus === 'won'
                  ? 'text-green-600 hover:bg-green-50'
                  : 'text-brand-green hover:bg-green-50'
              }`}
            >
              {transitioningTo === toStatus ? (
                <span className="inline-flex items-center gap-1">
                  <span
                    className="w-2.5 h-2.5 rounded-full border-2 border-current border-t-transparent animate-spin"
                    aria-hidden="true"
                  />
                  {ACTION_LABEL[toStatus]}
                </span>
              ) : (
                ACTION_LABEL[toStatus]
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Column ────────────────────────────────────────────────────────────────────

function Column({
  label,
  count,
  colorClass,
  children,
}: {
  label: string;
  count: number;
  colorClass: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-3 min-w-[220px] w-[220px] shrink-0">
      <div className="flex items-center gap-2">
        <h3 className={`text-xs font-semibold uppercase tracking-wide ${colorClass}`}>{label}</h3>
        <span
          className="text-xs text-gray-400 bg-gray-100 rounded-full px-2 py-0.5"
          aria-label={`${count} items`}
        >
          {count}
        </span>
      </div>
      <div className="flex flex-col gap-2">{children}</div>
    </div>
  );
}

// ── Toast ─────────────────────────────────────────────────────────────────────

function Toast({ message, type }: { message: string; type: 'success' | 'error' }) {
  return (
    <div
      role="alert"
      className={`fixed bottom-6 right-6 z-50 px-4 py-3 rounded-xl shadow-lg text-sm font-medium max-w-xs ${
        type === 'error' ? 'bg-red-600 text-white' : 'bg-brand-ink text-white'
      }`}
    >
      {message}
    </div>
  );
}

// ── Upsell card ───────────────────────────────────────────────────────────────

function UpsellCard() {
  async function handleUpgrade() {
    const { url } = await api.billing.checkout('tier_2');
    window.location.href = url;
  }

  return (
    <div className="flex flex-col items-center justify-center py-20 gap-4">
      <div className="w-12 h-12 rounded-full bg-amber-50 flex items-center justify-center text-2xl">
        🔗
      </div>
      <div className="text-center max-w-xs">
        <p className="font-semibold text-brand-ink">Outreach Pipeline</p>
        <p className="text-sm text-gray-400 mt-1">
          Outreach Pipeline is included in Pro and Agency plans. Automate your link-building
          outreach with AI-drafted emails and a visual pipeline.
        </p>
      </div>
      <button
        onClick={() => void handleUpgrade()}
        className="bg-brand-green text-white font-medium px-6 py-2.5 rounded-lg text-sm hover:bg-brand-green-dark transition-colors"
      >
        Upgrade to Pro
      </button>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

interface Props {
  domainId: string;
  tier: string;
}

export function OutreachPipeline({ domainId, tier }: Props) {
  const [targets, setTargets] = useState<BacklinkTarget[]>([]);
  const [drafts, setDrafts] = useState<OutreachDraft[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [generatingTargetId, setGeneratingTargetId] = useState<string | null>(null);
  const [confirmDismiss, setConfirmDismiss] = useState<BacklinkTarget | null>(null);
  const [transitioningDraftId, setTransitioningDraftId] = useState<string | null>(null);
  const [transitioningTo, setTransitioningTo] = useState<OutreachDraftStatus | null>(null);
  const [discardedExpanded, setDiscardedExpanded] = useState(false);
  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);
  const [modalDraft, setModalDraft] = useState<OutreachDraft | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const tierRank = TIER_RANK[tier] ?? 0;

  function showToast(msg: string, type: 'success' | 'error' = 'success') {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  }

  const fetchData = useCallback(async () => {
    try {
      const [targetsRes, draftsRes] = await Promise.all([
        api.backlinkTargets.listByDomain(domainId),
        api.outreachDrafts.listByDomain(domainId),
      ]);
      setTargets(targetsRes.targets);
      setDrafts(draftsRes);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load outreach data');
    }
  }, [domainId]);

  useEffect(() => {
    if (tierRank < 2) return;
    setLoading(true);
    fetchData().finally(() => setLoading(false));
  }, [fetchData, tierRank]);

  // 60-second background polling (Pro/Agency only)
  useEffect(() => {
    if (tierRank < 2) return;
    pollRef.current = setInterval(() => {
      void fetchData();
    }, POLL_MS);
    return () => {
      if (pollRef.current !== null) clearInterval(pollRef.current);
    };
  }, [fetchData, tierRank]);

  async function handleGenerate(targetId: string) {
    setGeneratingTargetId(targetId);
    try {
      const draft = await api.outreachDrafts.generate(targetId);
      setDrafts((prev) => [draft, ...prev]);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Generation failed';
      if (msg.includes('rate_limit') || msg.includes('draft_limit')) {
        showToast('Daily draft limit reached. Try again tomorrow.', 'error');
      } else {
        showToast(msg || 'Generation failed', 'error');
      }
    } finally {
      setGeneratingTargetId(null);
    }
  }

  async function handleDismissConfirm() {
    if (!confirmDismiss) return;
    const targetId = confirmDismiss.id;
    setConfirmDismiss(null);
    try {
      await api.backlinkTargets.dismiss(targetId);
      setTargets((prev) => prev.filter((t) => t.id !== targetId));
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Dismiss failed', 'error');
    }
  }

  async function handleStatusTransition(draftId: string, toStatus: OutreachDraftStatus) {
    const snapshot = drafts;

    // Optimistic update
    setDrafts((prev) =>
      prev.map((d) => (d.id === draftId ? { ...d, status: toStatus } : d)),
    );
    setTransitioningDraftId(draftId);
    setTransitioningTo(toStatus);

    try {
      const updated = await api.outreachDrafts.updateStatus(draftId, toStatus);
      setDrafts((prev) => prev.map((d) => (d.id === draftId ? updated : d)));
    } catch (err) {
      // Rollback
      setDrafts(snapshot);
      const msg = err instanceof Error ? err.message : 'Transition failed';
      if (msg.includes('invalid_transition')) {
        showToast('Illegal transition — that status change is not allowed.', 'error');
      } else {
        showToast(msg || 'Status update failed', 'error');
      }
    } finally {
      setTransitioningDraftId(null);
      setTransitioningTo(null);
    }
  }

  // ── Tier upsell ────────────────────────────────────────────────────────────

  if (tierRank < 2) return <UpsellCard />;

  // ── Loading / Error ────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-5 h-5 rounded-full border-2 border-brand-green border-t-transparent animate-spin" />
      </div>
    );
  }

  if (error) return <p className="text-sm text-red-500">{error}</p>;

  // ── Build column data ──────────────────────────────────────────────────────

  const targetMap: Record<string, BacklinkTarget> = Object.fromEntries(
    targets.map((t) => [t.id, t]),
  );

  const draftedTargetIds = new Set(drafts.map((d) => d.backlinkTargetId));
  const targetsWithoutDraft = targets.filter((t) => !draftedTargetIds.has(t.id));

  const draftsByStatus = (status: OutreachDraftStatus) =>
    drafts.filter((d) => d.status === status);

  const discardedDrafts = draftsByStatus('discarded');

  const totalItems =
    targets.length + drafts.filter((d) => d.status !== 'discarded').length;

  // ── Empty state ────────────────────────────────────────────────────────────

  if (totalItems === 0) {
    return (
      <section className="bg-white border border-gray-100 rounded-xl p-8 shadow-sm text-center space-y-3">
        <p className="text-sm text-gray-500">
          No outreach activity yet. Targets appear here once Backlinks discovery runs (typically
          within 24 hours of adding a competitor).
        </p>
      </section>
    );
  }

  const hasNoDrafts = drafts.filter((d) => d.status !== 'discarded').length === 0;

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <section className="space-y-4" id="outreach-pipeline">
      {/* Header */}
      <div>
        <h2 className="font-semibold text-brand-ink text-sm">Outreach Pipeline</h2>
        <p className="text-xs text-gray-400 mt-0.5">
          AI-drafted outreach emails for domains that link to your competitors.
        </p>
      </div>

      {/* Targets-only helper banner */}
      {hasNoDrafts && targetsWithoutDraft.length > 0 && (
        <div className="bg-amber-50 border border-amber-100 rounded-xl px-4 py-3 text-xs text-amber-700">
          These domains link to your competitors but not yet to you. Click{' '}
          <strong>Generate draft</strong> to start an outreach.
        </div>
      )}

      {/* Pipeline board — horizontal scroll on mobile */}
      <div className="overflow-x-auto pb-4 -mx-1 px-1">
        <div className="flex gap-4" style={{ minWidth: 'max-content' }}>
          {/* Targets column */}
          <Column
            label="Targets"
            count={targetsWithoutDraft.length}
            colorClass="text-gray-600"
          >
            {targetsWithoutDraft.length === 0 ? (
              <p className="text-xs text-gray-400 py-2">No uncontacted targets</p>
            ) : (
              targetsWithoutDraft.map((target) => (
                <TargetCard
                  key={target.id}
                  target={target}
                  generating={generatingTargetId === target.id}
                  onGenerate={() => {
                    void handleGenerate(target.id);
                  }}
                  onDismiss={() => setConfirmDismiss(target)}
                />
              ))
            )}
          </Column>

          {/* Status columns */}
          {COLUMN_STATUSES.map((status) => {
            const col = draftsByStatus(status);
            return (
              <Column
                key={status}
                label={status.charAt(0).toUpperCase() + status.slice(1)}
                count={col.length}
                colorClass={COLUMN_HEADER_COLOR[status]}
              >
                {col.length === 0 ? (
                  <p className="text-xs text-gray-400 py-2">—</p>
                ) : (
                  col.map((draft) => (
                    <DraftCard
                      key={draft.id}
                      draft={draft}
                      targetDomain={
                        targetMap[draft.backlinkTargetId]?.targetDomain ?? '(dismissed)'
                      }
                      transitioningTo={
                        transitioningDraftId === draft.id ? transitioningTo : null
                      }
                      onTransition={(toStatus) => {
                        void handleStatusTransition(draft.id, toStatus);
                      }}
                      onCardClick={() => setModalDraft(draft)}
                    />
                  ))
                )}
              </Column>
            );
          })}
        </div>
      </div>

      {/* Discarded strip */}
      {discardedDrafts.length > 0 && (
        <div className="border border-gray-100 rounded-xl overflow-hidden">
          <button
            onClick={() => setDiscardedExpanded((v) => !v)}
            className="w-full flex items-center justify-between px-4 py-3 text-xs text-gray-500 hover:bg-gray-50 transition-colors"
            aria-expanded={discardedExpanded}
          >
            <span>Discarded ({discardedDrafts.length})</span>
            <span aria-hidden="true">{discardedExpanded ? '▲' : '▼'}</span>
          </button>
          {discardedExpanded && (
            <div className="p-4 border-t border-gray-100 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {discardedDrafts.map((draft) => (
                <DraftCard
                  key={draft.id}
                  draft={draft}
                  targetDomain={
                    targetMap[draft.backlinkTargetId]?.targetDomain ?? '(dismissed)'
                  }
                  transitioningTo={null}
                  onTransition={() => {
                    /* no-op: discarded is terminal */
                  }}
                  onCardClick={() => {
                    /* no-op: terminal */
                  }}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Dismiss confirm modal */}
      {confirmDismiss !== null && (
        <ConfirmDismissModal
          target={confirmDismiss}
          onConfirm={() => {
            void handleDismissConfirm();
          }}
          onCancel={() => setConfirmDismiss(null)}
        />
      )}

      {/* Draft approval / edit modal (Prompt 7) */}
      {modalDraft !== null && (
        <OutreachDraftModal
          draft={modalDraft}
          target={targetMap[modalDraft.backlinkTargetId] ?? null}
          isOpen
          onClose={() => setModalDraft(null)}
          onDraftUpdated={(updated) => {
            setDrafts((prev) => prev.map((d) => (d.id === updated.id ? updated : d)));
            setModalDraft(updated);
          }}
          onToast={showToast}
        />
      )}

      {/* Toast */}
      {toast !== null && <Toast message={toast.msg} type={toast.type} />}
    </section>
  );
}
